import { useMemo } from 'react';
import { supabaseClient, supabaseCoreClient } from '../supabase';
import { useQuery, useDeleteMutation } from "@supabase-cache-helpers/postgrest-react-query";
import { encode } from "@supabase-cache-helpers/postgrest-react-query";
import { applyFilters } from '../filter';
import { queryClient } from '../constants';
import { commonQueryProps } from './cache';
import { fetchStructuralPage } from '../structuralPageQuery.js';
import { queryPolicies } from '../queryPolicies.js';
import { smartCast } from '../type-guards';
// PostgrestBuilder removed in newer Supabase versions
import type { 
  TableInfo, 
  ProcessedTableMetadata,
} from '../../types/metadata';
import type { BaseRecord } from '../../types/api';
import type { QueryMeta } from '@tanstack/react-query';
import type { Database } from '../../types/database.types';
import type { 
  QueryConditions, 
  OrderByOption, 
  LimitOffsetOption,
  CachedQueryResult
} from '../../types/supabase';
import { persistedRelations, useMetadataBootReady } from '../persistentMetadataCache.js';

export const clientForSchema = (schema: string) => (
  schema === 'core_crm' ? supabaseCoreClient : supabaseClient.schema(schema as any)
);

type RawTableInfo = Database['core_crm']['Tables']['table_info']['Row'];

// Build the Supabase query for fetching tables
export const buildQueryForTables = () => {
  return supabaseCoreClient
    .from('table_info')
    .select('*');
};

/**
 * Post-processes table data into a structured format for easy access
 * @param data - Raw table data from the database
 * @returns Processed table metadata with multiple access patterns
 */
export const postProcessTables = (data: RawTableInfo[] | null): ProcessedTableMetadata => {
  const groupedBy: ProcessedTableMetadata = {
    flattened: {},
    byId: {},
    asList: [],
    byName: {}
  };
  
  if (!data || !Array.isArray(data)) {
    return groupedBy;
  }
  
  data.forEach(rawTable => {
    const uiData = rawTable.ui_data && typeof rawTable.ui_data === 'object' && !Array.isArray(rawTable.ui_data)
      ? rawTable.ui_data
      : {};
    const table: TableInfo = {
      ...rawTable,
      ui_label: rawTable.ui_label ?? rawTable.table_name,
      ui_help: rawTable.ui_help ?? undefined,
      ui_data: uiData,
      client_side: rawTable.client_side ?? false,
    };
    groupedBy.asList.push(table);
    groupedBy.flattened[table.table_name] = table;
    (groupedBy as any)[table.table_name] = table; // Dynamic property assignment
    groupedBy.byId[table.id] = table;
    if (groupedBy.byName) {
      groupedBy.byName[table.table_name] = table;
    }
  });

  return groupedBy;
};

// Result type for table operations
interface TableResult {
  data: ProcessedTableMetadata | null;
  error: any;
}

/**
 * Fetches tables data outside of React context
 * @returns Promise with processed table data or error
 */
export const getTables = async (): Promise<TableResult> => {
  const query = buildQueryForTables();
  
  let tableData: RawTableInfo[] | null = null;
  let tableError: any = null;
  
  try {
    // TypeScript cannot infer the Supabase cache structure - see SupabaseCacheData type definition
    const cachedResult = queryClient.getQueryData(encode(query, false)) as CachedQueryResult<RawTableInfo[]>;
    tableData = cachedResult?.data ?? null;
  } catch {
    // Cache misses fall through to the API request below.
  }
  
  if (!tableData) {
    const { data, error } = await query;
    tableData = data;
    tableError = error;
  }

  if (tableError) {
    return { data: null, error: tableError };
  }

  const processedData = postProcessTables(tableData);

  return { data: processedData, error: null };
};

// Hook options interface
interface UseTablesOptions {
  enabled?: boolean;
}

// Hook result interface
interface UseTablesResult {
  data: ProcessedTableMetadata | null;
  error: any;
  isLoading: boolean;
}

/**
 * React hook to fetch tables data using React Query
 * @param options - Options including enabled flag
 * @returns Table data with loading and error states
 */
export const useTables = ({ enabled }: UseTablesOptions): UseTablesResult => {
  const bootReady = useMetadataBootReady();
  const query = buildQueryForTables();

  const { data, error, isLoading } = useQuery(
    query,
    {
      ...commonQueryProps,
      enabled: enabled && bootReady,
      meta: persistedRelations('core_crm.table_info')
    }
  );
  // Memoized on the query result so the processed object keeps a stable
  // identity across renders — consumers memoize off it.
  const processedData = useMemo(() => (data ? postProcessTables(data) : null), [data]);

  if (isLoading || error || !data) {
    return {
      data: null,
      error,
      isLoading
    };
  }

  return {
    data: processedData,
    error: null,
    isLoading: false
  };
};

// Parameters for building field-dependent table queries
interface BuildFieldDependentTableParams {
  tableName: string;
  tableInfo?: TableInfo;
  conditions?: QueryConditions;
  orderBy?: OrderByOption;
  limitOffset?: LimitOffsetOption;
  withCount?: boolean;
  deep?: boolean;
  schema: string;
  selectExpression?: string;
  source?: 'view' | 'deepView' | 'base';
  /**
   * REG-664: expanded relationship columns (`*_ref`) to include in the select.
   *
   * An expanded relationship adds the target's whole field list to the view, so
   * selecting every view column would roughly double the payload of a submission
   * or merchant page whether or not any of those columns is on screen. They are
   * therefore excluded by default and requested explicitly by the caller that
   * renders them. Ordering and filtering do not need this: PostgREST accepts a
   * column in `order` or a filter without it being selected.
   */
  refColumns?: readonly string[];
  /**
   * Leave out calculated (`*_calc`) view columns. They are often per-row
   * aggregates (funder_view's submission counts are nearly all of its query
   * time), so callers that only need labels and plain attributes, such as the
   * client-side preload, skip them.
   */
  excludeCalculated?: boolean;
}

/** Expanded relationship columns are suffixed `_ref` by the view generator. */
const isRefColumn = (column: string): boolean => column.endsWith('_ref');

// Result type for query building
interface BuildQueryResult {
  query: any; // PostgrestBuilder compatible query
}

/** The view (or base table) a grid reads for `source`. */
export const fieldDependentRelation = (
  tableName: string,
  source?: 'view' | 'deepView' | 'base',
  deep = false,
): string => (
  source === 'base'
    ? tableName
    : source === 'deepView' || deep
      ? `${tableName}_deep_view`
      : `${tableName}_view`
);

/**
 * Builds a Supabase query for fetching field-dependent table data with dynamic view columns
 * @param params - Parameters for building the query
 * @returns Object containing the built Supabase query
 */
export const buildQueryForFieldDependentTable = ({
  tableName,
  tableInfo,
  conditions,
  orderBy,
  limitOffset,
  withCount = false,
  deep = false,
  schema,
  selectExpression,
  source,
  refColumns,
  excludeCalculated = false,
}: BuildFieldDependentTableParams): BuildQueryResult => {

  const viewName = fieldDependentRelation(tableName, source, deep);

  const requestedRefColumns = new Set<string>(refColumns ?? []);
  const viewColumns = tableInfo?.ui_data?.view_columns
    ?.filter((column: string) => !isRefColumn(column) || requestedRefColumns.has(column))
    .filter((column: string) => !excludeCalculated || !column.endsWith('_calc'));

  const finalSelectExpression = source === 'base'
    ? selectExpression || '*'
    : selectExpression || viewColumns?.join(',') || '*';
  let supabaseQuery = (clientForSchema(schema) as any)
    .from(viewName)
    .select(finalSelectExpression, withCount ? { count: 'exact' } : undefined);

  if (conditions) {
    supabaseQuery = applyFilters(supabaseQuery, conditions);
  }

  // according to the docs, the way structure order by is:
  // .order('name', { referencedTable: 'cities', ascending: false })
  // but docs are out of date.
  // see https://github.com/supabase/supabase-js/issues/971#issuecomment-1937183596 which says:
  // to use: order('cities(name)', { ascending: false })
  if (orderBy) {
    // dynamically build the call to supabase .order function.
    // Descending date sorts must not float NULL values to the top (Postgres
    // defaults DESC to NULLS FIRST), and equal keys need a stable id
    // tiebreaker or server-side pagination can repeat/skip rows (REG-648).
    const ascending = orderBy.direction === 'ascending';
    const ascendingObj = ascending ? { ascending } : { ascending, nullsFirst: false };

    // docs are out of date
    const parts = orderBy.column.split('.');
    if (parts.length > 1) {
       // column is in the structure for related tables.
      // we are given a sort like 'merchant_rel.business_name' where the column merchant_rel is like {{"business_name":"foo"}}
      // we need to convert this to 'merchant_rel->>business_name'
      orderBy.column = `${parts[0]}->>${parts[1]}`;
    }

    supabaseQuery = supabaseQuery.order(orderBy.column, ascendingObj);
    if (orderBy.column !== 'id') {
      supabaseQuery = supabaseQuery.order('id', { ascending: false });
    }

  } else {
    // default to order by id descending
    supabaseQuery = supabaseQuery.order('id', { ascending: false });
  }
  
  // from the docs:
  // supabase.from('countries').select('name').range(0, 1)
  // Limit the query result by starting at an offset (from) and ending at the offset (from + to).
  // Only records within this range are returned.
  // This respects the query order and if there is no order clause the range could behave unexpectedly.
  // The from and to values are 0-based and inclusive: range(1, 3) will include the second, third
  // and fourth rows of the query.
  if (limitOffset) {
    const defaultLimit = 100;
    const defaultOffset = 0;
    let limit = defaultLimit;
    let offset = defaultOffset;
 
    if (limitOffset.limit) {
      limit = limitOffset.limit;
    }
    if (limitOffset.offset) {
      offset = limitOffset.offset;
    }
    supabaseQuery = supabaseQuery.range(offset, offset + limit);
  }
  return { query: supabaseQuery };
};

// Parameters for getFieldDependentTable function
interface GetFieldDependentTableParams {
  tableName: string;
  conditions?: QueryConditions;
  orderBy?: OrderByOption;
  limitOffset?: LimitOffsetOption;
  withCount?: boolean;
  singleRow?: boolean;
  noCache?: boolean;
  deep?: boolean;
  schema: string;
  selectExpression?: string;
  source?: 'view' | 'deepView' | 'base';
  /** REG-664: expanded relationship columns to include; see BuildFieldDependentTableParams. */
  refColumns?: readonly string[];
  /** See BuildFieldDependentTableParams. */
  excludeCalculated?: boolean;
  /** Navigation warming only: do nothing while this exact structural key still exists. */
  onlyIfAbsent?: boolean;
  /** Already-rendered metadata avoids turning hover into a metadata refresh trigger. */
  tableMetadata?: ProcessedTableMetadata;
  /** Override the shared paged-data policy for a non-grid structural request. */
  staleTime?: number;
  gcTime?: number;
  /** Query meta, e.g. persistedClientSideTable for the client-side preload. */
  meta?: QueryMeta;
}

// Result type for field-dependent table operations
interface FieldDependentTableResult<T = any> {
  data: T | T[] | null;
  error: any;
  count: number | null;
}

/**
 * Fetches field-dependent table data outside of React context
 * @param params - Parameters for fetching the data
 * @returns Promise with data, error, and count
 */
export const getFieldDependentTable = async <T extends BaseRecord>({
  tableName,
  conditions,
  orderBy,
  limitOffset,
  withCount,
  singleRow,
  noCache,
  deep = false,
  schema,
  selectExpression,
  source,
  refColumns,
  excludeCalculated,
  onlyIfAbsent = false,
  tableMetadata,
  staleTime,
  gcTime,
  meta,
}: GetFieldDependentTableParams): Promise<FieldDependentTableResult<T>> => {

  if (!schema) {
    throw new Error(`Schema parameter is required for getFieldDependentTable. Missing schema for table: ${tableName}`);
  }
  
  const { data: tableData, error: tableError } = tableMetadata
    ? { data: tableMetadata, error: null }
    : await getTables();

  if (tableError) {
    return { data: null, error: tableError , count:null};
  }

  const tableInfo = tableData?.byName?.[tableName];
  
  const queryParams = {
    tableName,
    tableInfo,
    conditions,
    orderBy,
    limitOffset,
    withCount,
    deep,
    schema,
    selectExpression,
    source,
    refColumns,
    excludeCalculated,
  };
  const { query } = buildQueryForFieldDependentTable(queryParams);
  const separateCount = withCount && tableInfo?.id && source !== 'base'
    && (!conditions || Object.keys(conditions).length === 0);
  const fetchPage = async () => {
    if (!separateCount) return await query;
    const [page, total] = await Promise.all([
      buildQueryForFieldDependentTable({ ...queryParams, withCount: false }).query,
      (supabaseCoreClient as any).rpc('count_table_records', { p_table_info_id: tableInfo.id }),
    ]);
    return { ...page, count: total.error ? null : total.data, error: page.error ?? total.error };
  };

  const queryCacheKey = encode(query, false);
  let result: NonNullable<CachedQueryResult>;

  try {
    const existing = onlyIfAbsent
      ? queryClient.getQueryCache().find({ queryKey: queryCacheKey, exact: true })
      : undefined;
    if (existing) {
      const cached = queryClient.getQueryData<NonNullable<CachedQueryResult>>(queryCacheKey);
      return cached
        ? {
            data: smartCast<T>(cached.data, schema, tableName),
            error: null,
            count: cached.count ?? null,
          }
        : { data: null, error: null, count: null };
    }

        result = noCache
      ? await fetchPage()
      : await fetchStructuralPage<NonNullable<CachedQueryResult>>({
          queryKey: queryCacheKey,
          queryFn: async () => {
            const response = await fetchPage();
            if (response.error) throw response.error;
            return response;
          },
          staleTime,
          gcTime,
          meta,
        });
  } catch (error) {
    return { data: null, error, count: null };
  }

  const { data, count, error } = result;
  if (error) {
    return { data: null, error, count:null };
  }

  let returnData;
  if (singleRow && data && data.length > 0) {
    returnData = data[0];
  } else {
    returnData = data;
  }

  // Apply smart casting for core_crm tables while preserving BaseRecord for user tables
  const typedData = smartCast<T>(returnData, schema, tableName);
  
  return {
    data: typedData,
    error: null,
    count: count ?? null
  };
};

// Parameters for useFieldDependentTable hook
interface UseFieldDependentTableParams {
  enabled?: boolean;
  tableName: string;
  conditions?: QueryConditions;
  orderBy?: OrderByOption;
  limitOffset?: LimitOffsetOption;
  withCount?: boolean;
  singleRow?: boolean;
  schema: string;
  deep?: boolean;
  source?: 'view' | 'deepView' | 'base';
  /** Override the shared cache freshness window for record detail views. */
  staleTime?: number;
  /** Refresh stale operational screens when the user returns to the tab. */
  refetchOnWindowFocus?: boolean;
}

// Result type for useFieldDependentTable hook
export interface UseFieldDependentTableResult<T = any> {
  data: T | T[] | null;
  error: any;
  isLoading: boolean;
  isFetching: boolean;
  count: number | null;
}

/**
 * React hook to fetch field-dependent table data using React Query
 * @param params - Parameters for the hook
 * @returns Hook result with data, loading, and error states
 */
export const useFieldDependentTable = <T extends BaseRecord>({
  enabled,
  tableName,
  conditions,
  orderBy,
  limitOffset,
  withCount,
  singleRow,
  schema,
  deep = false,
  source,
  staleTime,
  refetchOnWindowFocus,
}: UseFieldDependentTableParams): UseFieldDependentTableResult<T> => {

  if(!schema){
    throw Error(`useFieldDependentTable(tableName='${tableName}') called with no schema. This is no longer supported`)
  }


  const {
    data: tableData,
    error: tableError,
    isLoading: tableIsLoading
  } = useTables({ enabled });

  const tableInfo = tableData?.byName?.[tableName];

  // dummy query since useQuery type checks to ensure PostgrestBuilder
  let query = (clientForSchema(schema) as any).from(tableName).select('*')
  if(enabled){
    // guard this somewhat expensive operation
    query = buildQueryForFieldDependentTable({
      tableName,
      tableInfo,
      conditions,
      orderBy,
      limitOffset,
      withCount,
      deep,
      schema,
      source
    }).query;
  }

  const {
    data: queryData,
    count: countData,
    error: queryError,
    isLoading: queryIsLoading,
    isFetching: queryIsFetching
  } = useQuery(
    query,
    {
      ...commonQueryProps,
      staleTime: staleTime ?? commonQueryProps.staleTime,
      gcTime: staleTime === queryPolicies.recordDetail.staleTime
        ? queryPolicies.recordDetail.gcTime
        : commonQueryProps.gcTime,
      refetchOnWindowFocus: refetchOnWindowFocus ?? commonQueryProps.refetchOnWindowFocus,
      enabled: !tableError && !tableIsLoading && enabled
    }
  );

  let returnData;
  if (singleRow) {
    // Guard against corrupted cache entries where queryData is [undefined]
    const firstRow = Array.isArray(queryData) ? queryData[0] : null;
    returnData = firstRow != null ? firstRow : null;
  } else {
    returnData = queryData;
  }

  // Apply smart casting for core_crm tables while preserving BaseRecord for user tables
  const typedData = smartCast<T>(returnData, schema, tableName);

  return {
    data: typedData,
    error: tableError || queryError,
    isLoading: queryIsLoading || tableIsLoading,
    isFetching: queryIsFetching || tableIsLoading,
    count: countData ?? null
  };
};

/**
 * Hook for deleting records by ID
 * @param tableName - Name of the table to delete from
 * @param onSuccess - Success callback function
 * @param onFailure - Failure callback function
 * @returns Delete function that can be called with an ID
 */
export const useDeleteById = (
  tableName: string,
  onSuccess?: (data: any) => void,
  onFailure?: (error: any) => void
) => {
  const { mutateAsync: deleteRow } = useDeleteMutation(
    (supabaseClient as any).from(tableName),
    ['id'],
    'id', // don't really understand this parameter
    {
      onSuccess,
      onError: onFailure
    }
  );

  return deleteRow;
};
